import React, { cloneElement, useEffect, useMemo, useRef, useState } from 'react'
import * as Dialog from "@radix-ui/react-dialog";
import { useTranslation } from 'react-i18next';
import { X } from "lucide-react";
import { cn } from '@/lib/utils';
import { IconButton } from '../common/icon-button';
import { DouyinIcon, KuaishouIcon, PublishIcon, ShareIcon, TiktokIcon, WeChatOfficialAccountIcon, XiaoHongShuIcon, XiaoYuZhouImage, XiMalayaIcon } from './assets';
import { Tooltip } from '../ui/modal';
import { Image } from '../common/image';
import { shareHook } from '@/manager';
import { LoadingOverlay } from '../common/loading-overlay';
import { QRCodeCanvas } from 'qrcode.react';

interface AgentShareModalProps {
  trigger?: React.ReactElement;
  open?: boolean;
  loading?: boolean;
  /** 是否在外部控制时触发数据获取（例如父组件打开时） */
  shouldFetchData?: boolean;
  /** 是否展示抖音 */
  showDouyin?: boolean;
  /** 抖音分享选项 */
  douyinOptions?: {
    videoUrls?: string[];
    imageUrls?: string[];
  }
  /** 是否展示tiktok */
  showTiktok?: boolean;
  /** tiktok分享选项 */
  tiktokOptions?: {
    videoUrls?: string[];
  }
  /** 是否展示快手 */
  showKuaishou?: boolean;
  /** 快手分享选项 */
  kuaishouOptions?: {
    title: string;
    coverUrl: string;
    videoUrl: string;
  }
  /** 是否展示微信公众号平台 */
  showWeChatOfficialAccount?: boolean;
  /** 是否展示小红书 */
  showXiaoHongShu?: boolean;
  /** 是否展示喜马拉雅 */
  showXiMalaya?: boolean;
  /** 是否展示小宇宙 */
  showXiaoyuzhou?: boolean;
  /** 抖音点击回调 */
  onDouyinClick?: () => void;
  /** 快手点击回调 */
  onKuaishouClick?: () => void;
  /** 微信公众号平台点击回调 */
  onWeChatOfficialAccountClick?: () => void;
  /** 小红书点击回调 */
  onXiaoHongShuClick?: () => void;
  /** 喜马拉雅点击回调 */
  onXiMalayaClick?: () => void;
  /** 小宇宙点击回调 */
  onXiaoyuzhouClick?: () => void;
  onOpenChange?: (open: boolean) => void;
}

/** 分享弹窗 */
export const AgentShareModal: React.FC<AgentShareModalProps> = ({
  trigger,
  open: externalOpen,
  loading,
  shouldFetchData,
  showDouyin,
  douyinOptions,
  showTiktok,
  tiktokOptions,
  showKuaishou,
  kuaishouOptions,
  showWeChatOfficialAccount,
  showXiaoHongShu,
  showXiMalaya,
  showXiaoyuzhou,
  onDouyinClick,
  onKuaishouClick,
  onWeChatOfficialAccountClick,
  onXiaoHongShuClick,
  onXiMalayaClick,
  onXiaoyuzhouClick,
  onOpenChange,
}) => {
  const { t } = useTranslation();
  const [internalOpen, setInternalOpen] = useState(false);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const hasFetchedDataRef = useRef(false);

  // 分享到抖音H5
  const {
    shareToDouyin,
    scanUrl,
    loading: shareDouyinLoading,
    clearScanUrl
  } = shareHook.useShareToDouyin()

  // 分享到快手
  const {
    shareToKuaishou,
    getKuaishouThirdAccount,
    shareLoading: shareKuaishouLoading,
    thirdIdLoading: kuaishouThirdIdLoading,
  } = shareHook.useShareToKuaishou()

  // 分享到TikTok
  const {
    shareToTiktok,
    getTiktokThirdAccount,
    shareLoading: shareTiktokLoading,
    thirdIdLoading: tiktokThirdIdLoading,
  } = shareHook.useShareToTiktok()

  // 判断是否使用 trigger 模式（trigger 优先）
  const isTriggerMode = !!trigger;

  // 根据模式决定使用哪个 open 状态
  const currentOpen = isTriggerMode ? internalOpen : externalOpen ?? false;

  // 处理 open 状态变化
  const handleOpenChange = (open: boolean) => {
    if (isTriggerMode) {
      setInternalOpen(open);
    } else {
      onOpenChange?.(open);
    }
  };

  useEffect(() => {
    const shouldTriggerFetch = shouldFetchData || currentOpen;

    if (shouldTriggerFetch && !hasFetchedDataRef.current) {
      hasFetchedDataRef.current = true;

      // 初始化抖音分享
      if (showDouyin && (douyinOptions?.videoUrls?.length || douyinOptions?.imageUrls?.length)) {
        shareToDouyin({
          videoUrls: douyinOptions?.videoUrls || [],
          imageUrls: douyinOptions?.imageUrls || []
        });
      }

      // 初始化快手分享
      if (showKuaishou) {
        getKuaishouThirdAccount();
      }

      // 初始化tiktok分享
      if (showTiktok) {
        getTiktokThirdAccount();
      }
    }

    if (!currentOpen && !shouldFetchData) {
      clearScanUrl();
      hasFetchedDataRef.current = false;
    }
  }, [currentOpen, shouldFetchData])

  // 关闭
  const handleClose = () => {
    handleOpenChange(false);
  };

  // trigger 点击处理
  const handleTriggerClick = () => {
    handleOpenChange(true);
  };

  // 弹窗打开时，强制聚焦到标题
  useEffect(() => {
    if (currentOpen) {
      // 等待DOM更新后执行聚焦（确保元素已挂载）
      setTimeout(() => {
        titleRef.current?.focus();
      }, 0);
    }
  }, [currentOpen]);

  // 分享点击处理
  const handleShareClick = (platform: string) => {
    switch (platform) {
      case "douyin":
        onDouyinClick?.();
        break;
      case "tiktok":
        if (tiktokThirdIdLoading) return;
        if (!tiktokOptions?.videoUrls?.length) return;
        // 分享到tiktok
        shareToTiktok({
          videoUrls: tiktokOptions?.videoUrls || [],
        });
        break;
      case "kuaishou":
        if (kuaishouThirdIdLoading) return;
        if (!kuaishouOptions?.title || !kuaishouOptions?.coverUrl || !kuaishouOptions?.videoUrl?.length) return;
        // 分享到快手
        shareToKuaishou({
          brief: kuaishouOptions?.title,
          coverUrl: kuaishouOptions?.coverUrl,
          fileUrl: kuaishouOptions?.videoUrl,
        });
        break;
      case "wechat_official_account":
        if (onWeChatOfficialAccountClick) {
          onWeChatOfficialAccountClick?.();
        } else {
          window.open("https://mp.weixin.qq.com/", "_blank");
        }
        break;
      case "xiaohongshu":
        if (onXiaoHongShuClick) {
          onXiaoHongShuClick?.();
        } else {
          window.open("https://creator.xiaohongshu.com/", "_blank");
        }
        break;
      case "ximalaya":
        if (onXiMalayaClick) {
          onXiMalayaClick?.();
        } else {
          window.open("https://www.ximalaya.com/", "_blank");
        }
        break;
      case "xiaoyuzhou":
        if (onXiaoyuzhouClick) {
          onXiaoyuzhouClick?.();
        } else {
          window.open("https://podcaster.xiaoyuzhoufm.com/", "_blank");
        }
        break;
      default:
        break;
    }
  }

  // 平台列表
  const platformList = useMemo(() => {
    return [
      {
        id: "douyin",
        show: showDouyin,
        name: t("agent_share_modal.douyin"),
        icon: (
          <Tooltip
            side='top'
            align='center'
            trigger={
              <div><DouyinIcon /></div>
            }
            content={
              <div className='relative flex flex-col gap-2 bg-white p-3 rounded-[12px] w-[164px] min-h-[192px] max-h-max [box-shadow:0px_4px_8px_rgba(0,_0,_0,_0.2)]'>
                <div className='w-full h-[140px] relative'>
                  <LoadingOverlay loading={shareDouyinLoading} />
                  {!shareDouyinLoading && scanUrl && (
                    <QRCodeCanvas
                      value={scanUrl}
                      size={140} // 建议尺寸大一点，因为 URL 包含大量参数
                      level={"L"} // 容错等级 (L, M, Q, H)，链接越长建议用 L 减少复杂度
                    />
                  )}
                </div>
                <div className='text-xs text-[rgba(52,64,84,1)] leading-[17px]'>
                  {t("agent_share_modal.douyin_desc1")}<br />
                  {t("agent_share_modal.douyin_desc2")}
                </div>
              </div>
            }
          />
        ),
      },
      {
        id: "tiktok",
        show: showTiktok,
        name: t("agent_share_modal.tiktok"),
        icon: <div className='relative min-h-[58px] min-w-[58px]'>
          {<LoadingOverlay loading={shareTiktokLoading || tiktokThirdIdLoading} showIcon={!tiktokThirdIdLoading} />}
          <TiktokIcon />
        </div>,
      },
      {
        id: "kuaishou",
        show: showKuaishou,
        name: t("agent_share_modal.kuaishou"),
        icon: <div className='relative min-h-[58px] min-w-[58px]'>
          {<LoadingOverlay loading={shareKuaishouLoading || kuaishouThirdIdLoading} showIcon={!kuaishouThirdIdLoading} />}
          <KuaishouIcon />
        </div>,
      },
      {
        id: "wechat_official_account",
        show: showWeChatOfficialAccount,
        name: t("agent_share_modal.wechat_official_account"),
        icon: <WeChatOfficialAccountIcon />,
      },
      {
        id: "xiaohongshu",
        show: showXiaoHongShu,
        name: t("agent_share_modal.xiaohongshu"),
        icon: <XiaoHongShuIcon />,
      },
      {
        id: "ximalaya",
        show: showXiMalaya,
        name: t("agent_share_modal.ximalaya"),
        icon: <XiMalayaIcon />,
      },
      {
        id: "xiaoyuzhou",
        show: showXiaoyuzhou,
        name: t("agent_share_modal.xiaoyuzhou"),
        icon: <XiaoYuZhouImage className='size-[58px]' />,
      },
    ]
  }, [showDouyin, showKuaishou, showWeChatOfficialAccount, showXiaoHongShu, showXiMalaya, showXiaoyuzhou, t, scanUrl, shareDouyinLoading, shareKuaishouLoading, kuaishouThirdIdLoading, shareTiktokLoading, tiktokThirdIdLoading])

  const dialogContent = (
    <Dialog.Portal>
      <Dialog.Overlay className={cn("fixed inset-0 bg-black/50 z-50")} />
      <Dialog.Content
        className={cn(
          "flex flex-col",
          "fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2",
          "bg-white rounded-[24px] shadow-lg",
          "min-w-[330px] max-w-max max-h-[224px] min-h-[224px] flex-shrink-0",
          "z-50 overflow-hidden"
        )}
        onClick={e => e.stopPropagation()}
      >
        {/* 标题区域 */}
        <div className="flex max-h-[92px] justify-between items-center px-6 pt-6 pb-8">
          <Dialog.Title
            ref={titleRef}
            tabIndex={-1}
            className='focus:outline-none flex items-center gap-4'
          >
            <div className='flex items-center justify-center p-3 border border-[rgba(234,236,240,1)] rounded-[10px] [box-shadow:0px_1px_2px_rgba(16,_24,_40,_0.05)]'>
              <PublishIcon className='size-6 text-[rgba(16,24,40,1)] ' />
            </div>
            <span className='text-[rgba(16,24,40,1)] text-lg font-semibold'>{t("agent_share_modal.title")}</span>
          </Dialog.Title>
          <Dialog.Close asChild>
            <IconButton
              className='size-8'
              icon={<X className="size-6 text-[rgba(16,24,40,1)]" />}
              onClick={handleClose}
            />
          </Dialog.Close>
        </div>
        {/* 内容 */}
        <div className='flex-1 flex justify-center px-10 gap-8'>
          {platformList.filter(item => item.show).map(item => (
            <div
              className='flex flex-col items-center gap-2 cursor-pointer'
              onClick={() => handleShareClick(item.id)}
            >
              {item.icon}
              <span className='text-[rgba(24,29,39,1)] text-[13px] font-semibold'>
                {item.name}
              </span>
            </div>
          ))}
        </div>
      </Dialog.Content>
    </Dialog.Portal>
  )

  // trigger 模式
  if (isTriggerMode) {
    return (
      <Dialog.Root open={currentOpen} onOpenChange={handleOpenChange}>
        <Dialog.Trigger asChild>
          {cloneElement(trigger, {
            onClick: (e: React.MouseEvent) => {
              e.stopPropagation();
              e.preventDefault();
              handleTriggerClick();
              // 保留原有的 onClick 事件
              if (trigger.props.onClick) {
                trigger.props.onClick(e);
              }
            },
          })}
        </Dialog.Trigger>
        {dialogContent}
      </Dialog.Root>
    );
  }

  // open 参数模式
  return (
    <Dialog.Root open={currentOpen} onOpenChange={handleOpenChange}>
      {dialogContent}
    </Dialog.Root>
  );
}
